import {
  AttributeDataType,
  FileUploadedSchema,
  type FileUploadedType,
  type InputOutputSchemaPropertyConfigurationType,
} from "@validereinc/domain";
import { z } from "zod";

export const WorkerFileInputMode = {
  UPLOAD: "upload",
  REFERENCE: "reference",
} as const;

export type WorkerFileInputModeType =
  (typeof WorkerFileInputMode)[keyof typeof WorkerFileInputMode];

/**
 * Matches backend MULTI_FILE `validateMaxLength(..., 10)` and FileInput's
 * picker cap.
 */
export const WORKER_MULTI_FILE_MAX_COUNT = 10;

export const WORKER_MULTI_FILE_MAX_COUNT_ERROR = `A maximum of ${WORKER_MULTI_FILE_MAX_COUNT} files is allowed`;

export const getWorkerFileInputModeFieldName = (schemaKey: string) =>
  `__workerFileMode_${schemaKey}`;

export const getWorkerFileInputReferenceFieldName = (schemaKey: string) =>
  `__workerFileReference_${schemaKey}`;

const WorkerFileReferenceItemSchema = z.object({
  name: z.string().min(1, "File name is required"),
  ref: z.string().uuid("File ref must be a UUID"),
});

const toUploadedFiles = (rawValue: unknown): FileUploadedType[] => {
  if (Array.isArray(rawValue)) {
    return rawValue.flatMap((item) => {
      const parsed = FileUploadedSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    });
  }

  const parsed = FileUploadedSchema.safeParse(rawValue);
  return parsed.success ? [parsed.data] : [];
};

const formatZodFileReferenceError = (error: z.ZodError): string => {
  const issue = error.issues[0];
  if (!issue) {
    return "Enter a valid file reference";
  }

  return issue.message;
};

export type ParseWorkerFileReferenceJsonResultType =
  | { success: true; data: FileUploadedType[] }
  | { success: false; error: string };

/**
 * Parses a JSON string of `{ name, ref }` (FILE) or `[{ name, ref }, ...]`
 * (MULTI_FILE). `ref` must be a UUID.
 */
export const parseWorkerFileReferenceJson = (
  rawValue: unknown,
  options: { allowMultiple: boolean }
): ParseWorkerFileReferenceJsonResultType => {
  if (rawValue == null) {
    return { success: true, data: [] };
  }

  if (typeof rawValue !== "string") {
    return { success: false, error: "Enter valid JSON" };
  }

  const trimmed = rawValue.trim();
  if (trimmed === "") {
    return { success: true, data: [] };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { success: false, error: "Enter valid JSON" };
  }

  if (options.allowMultiple) {
    if (!Array.isArray(parsed)) {
      return {
        success: false,
        error: "Enter a JSON array of objects with name and ref",
      };
    }

    const result = z
      .array(WorkerFileReferenceItemSchema)
      .max(WORKER_MULTI_FILE_MAX_COUNT, WORKER_MULTI_FILE_MAX_COUNT_ERROR)
      .safeParse(parsed);
    if (!result.success) {
      return {
        success: false,
        error: formatZodFileReferenceError(result.error),
      };
    }

    return { success: true, data: result.data };
  }

  if (Array.isArray(parsed)) {
    return {
      success: false,
      error: "Enter a JSON object with name and ref",
    };
  }

  const result = WorkerFileReferenceItemSchema.safeParse(parsed);
  if (!result.success) {
    return { success: false, error: formatZodFileReferenceError(result.error) };
  }

  return { success: true, data: [result.data] };
};

/**
 * Serializes uploaded file values to the JSON the reference textarea uses.
 */
export const formatWorkerFileReferenceJson = (
  files: unknown,
  allowMultiple: boolean
): string => {
  const uploaded = toUploadedFiles(files);
  if (uploaded.length === 0) {
    return "";
  }

  return JSON.stringify(allowMultiple ? uploaded : uploaded[0], null, 2);
};

/**
 * React Hook Form validator for the file-reference textarea.
 */
export const validateWorkerFileReferenceJson = (
  rawValue: unknown,
  options: { allowMultiple: boolean; isRequired: boolean; label: string }
): true | string => {
  const parsed = parseWorkerFileReferenceJson(rawValue, {
    allowMultiple: options.allowMultiple,
  });

  if (!parsed.success) {
    return parsed.error;
  }

  if (options.isRequired && parsed.data.length === 0) {
    return `${options.label} is required`;
  }

  return true;
};

/**
 * Enforces the MULTI_FILE count cap for uploaded FileUploadInput values.
 */
export const validateWorkerMultiFileCount = (files: unknown): true | string => {
  if (!Array.isArray(files) || files.length <= WORKER_MULTI_FILE_MAX_COUNT) {
    return true;
  }

  return WORKER_MULTI_FILE_MAX_COUNT_ERROR;
};

/**
 * Converts a file form value to the payload shape `POST /worker_runs` expects.
 * Upload mode uses `{ ref, name }[]` from `FileUploadInput`; reference mode
 * parses the sibling JSON textarea. FILE fields need the first item and
 * MULTI_FILE fields need the array.
 */
export const normalizeWorkerRunFileInputValue = (
  rawValue: unknown,
  definition: InputOutputSchemaPropertyConfigurationType,
  formValues: Record<string, unknown> = {},
  schemaKey?: string
): FileUploadedType | FileUploadedType[] | undefined => {
  const isMultiFile = definition.data_type === AttributeDataType.MULTI_FILE;
  const mode =
    schemaKey != null
      ? formValues[getWorkerFileInputModeFieldName(schemaKey)]
      : undefined;

  if (mode === WorkerFileInputMode.REFERENCE && schemaKey != null) {
    const parsed = parseWorkerFileReferenceJson(
      formValues[getWorkerFileInputReferenceFieldName(schemaKey)],
      { allowMultiple: isMultiFile }
    );

    if (!parsed.success) {
      return isMultiFile ? [] : undefined;
    }

    return isMultiFile ? parsed.data : parsed.data[0];
  }

  const files = toUploadedFiles(rawValue);

  if (definition.data_type === AttributeDataType.FILE) {
    return files[0];
  }

  return files;
};
